
import { useState, useEffect, useMemo } from 'react';
import { MapLocation } from '@/types/location';
import { useLocationSelection } from './hooks/useLocationSelection';
import { useLocationFiltering } from './hooks/useLocationFiltering';
import { useLocationProcessing } from './hooks/useLocationProcessing';


interface UseDeviceMapStateProps {
  onDeviceSelect: (devices: number) => void;
  onLocationsChange: (locations: Array<{ id: number; name: string; devices: number; mediaOwner?: string }>) => void;
  importedLocations: Partial<MapLocation>[];
  originalLocations: MapLocation[];
}

export const useDeviceMapState = ({
  onDeviceSelect,
  onLocationsChange,
  importedLocations,
  originalLocations
}: UseDeviceMapStateProps) => {
  const [activeRegion, setActiveRegion] = useState<'all' | 'Riyadh' | 'Jeddah' | 'Eastern' | 'Medina' | 'Al Kharj' | 'Dawadmi' | 'Tabuk' | 'Hail' | 'Hafar Al Batin' | 'Majmaa' | 'Qassim' | 'Sakaka' | 'Arar' | 'Al Qurayyat' | 'Al Jubail' | 'Yanbu' | 'Khamis Mushait' | 'Sabia' | 'Abha' | 'Jizan' | 'Najran' | 'Muhayil' | 'Makkah' | 'Al Bahah'>('all');
  const [activeMediaKind, setActiveMediaKind] = useState<string>('all');
  const [activeScreenType, setActiveScreenType] = useState<string>('all');
  const [activeMediaOwner, setActiveMediaOwner] = useState<string>('all');
  const [attentionScreenCounts, setAttentionScreenCounts] = useState<Map<number, number>>(new Map());
  const [focusedLocation, setFocusedLocation] = useState<MapLocation | null>(null);

  // Process locations
  const { locations } = useLocationProcessing(originalLocations);

  // Apply filters
  const { filteredLocations } = useLocationFiltering(
    locations,
    activeRegion,
    activeMediaKind,
    activeScreenType,
    activeMediaOwner
  );

  // Initialize selected locations from imported locations
  const initialSelectedLocationIds = useMemo(() => {
    if (!importedLocations || importedLocations.length === 0) return new Set<number>();
    if (locations.length === 0) return new Set<number>();

    const selectedIds = new Set<number>();

    importedLocations.forEach(importedLoc => {
      // Prefer ID when provided
      if (typeof (importedLoc as any).id === 'number') {
        const byId = locations.find(l => l.id === (importedLoc as any).id);
        if (byId) { selectedIds.add(byId.id); return; }
      }

      const normalize = (s: any) =>
        (s ?? '')
          .toString()
          .replace(/^\s*\d+\s*[-–—.).]\s*/, '')
          .toLowerCase()
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^\p{L}\p{N}\s]/gu, '')
          .replace(/\bscreens\b/g, 'screen')
          .replace(/\s+/g, ' ')
          .trim();

      const normalizeRegion = (r?: string) => {
        const key = normalize(r);
        const map: Record<string, string> = {
          'al madinah': 'medina', 'madinah': 'medina', 'medinah': 'medina',
          'al madinah al munawwarah': 'medina', 'ar riyadh': 'riyadh'
        };
        return map[key] || key;
      };

      const normalizeOwner = (o?: string) => {
        const key = normalize(o);
        const map: Record<string, string> = { 'tableads': 'tableads', 'alan': 'alan', 'waki': 'waki', 'attention': 'attention' };
        return map[key] || key;
      };

      const nameKey = normalize(importedLoc.name);
      const regionKey = normalizeRegion((importedLoc as any).region);
      const ownerKey = normalizeOwner((importedLoc as any).mediaOwner);

      let candidates = locations.filter(loc => normalize(loc.name) === nameKey);
      if (regionKey) candidates = candidates.filter(loc => normalizeRegion(loc.region) === regionKey);
      if (ownerKey) candidates = candidates.filter(loc => normalizeOwner(loc.mediaOwner) === ownerKey);
      candidates.forEach(c => selectedIds.add(c.id));
    });

    return selectedIds;
  }, [importedLocations, locations]);

  // Location selection logic
  const {
    selectedLocations,
    handleToggleAllInRegion,
    handleToggleTypeInRegion,
    handleDeselectLocation,
    handleLocationSelect,
    refreshSelections,
    isRemoving
  } = useLocationSelection(
    filteredLocations,
    locations,
    onDeviceSelect,
    onLocationsChange,
    activeMediaOwner,
    initialSelectedLocationIds,
    attentionScreenCounts // Pass screen counts to selection hook
  );

  // Keep focusedLocation in sync with latest locations data (e.g. after a dashboard edit).
  // Uses functional setState to read current value without a stale closure dependency.
  useEffect(() => {
    setFocusedLocation(current => {
      if (!current) return current;
      const updated = locations.find(l => l.id === current.id);
      return updated && updated !== current ? updated : current;
    });
  }, [locations]);

  const handleAttentionScreenCountChange = (locationId: number, screenCount: number) => {
    setAttentionScreenCounts(prev => {
      const newMap = new Map(prev);
      newMap.set(locationId, screenCount);
      return newMap;
    });
  };
  
  // Trigger selection update when screen counts change
  useEffect(() => {
    if (attentionScreenCounts.size > 0 && selectedLocations.size > 0) {
      refreshSelections();
    }
  }, [attentionScreenCounts, selectedLocations.size]);

  return [
    {
      selectedLocations,
      activeRegion,
      locations,
      activeMediaKind,
      activeScreenType,
      activeMediaOwner,
      filteredLocations,
      attentionScreenCounts,
      isRemoving,
      focusedLocation
    },
    {
      setActiveRegion,
      setActiveMediaKind,
      setActiveScreenType,
      setActiveMediaOwner,
      handleToggleAllInRegion,
      handleToggleTypeInRegion,
      handleDeselectLocation,
      handleLocationSelect,
      handleAttentionScreenCountChange,
      setFocusedLocation
    }
  ] as const;
};
